import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/auth.store";
import { listJobs, discardJob } from "../../api/ingest.api";

const STATE_FILTERS = [
  { value: "",          label: "All" },
  { value: "REVIEW",    label: "Awaiting review" },
  { value: "EXTRACTING",label: "Extracting" },
  { value: "COMMITTED", label: "Committed" },
  { value: "ERRORED",   label: "Errored" },
  { value: "DISCARDED", label: "Discarded" },
];

const STATE_PILL = {
  UPLOADED:   "bg-slate-600/30 text-slate-300 border-slate-600/40",
  EXTRACTING: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  REVIEW:     "bg-amber-500/20 text-amber-300 border-amber-500/40",
  COMMITTED:  "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  DISCARDED:  "bg-zinc-600/30 text-zinc-400 border-zinc-600/40",
  ERRORED:    "bg-rose-500/20 text-rose-300 border-rose-500/40",
};

export default function IngestJobsList() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuthStore();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate("/login"); return; }
    if (!user?.is_ingest_allowed) {
      setError("You don't have access to this tool. Ask admin to enable it.");
      setLoading(false);
      return;
    }
    refresh(filter);
  }, [authLoading, isAuthenticated, filter]);

  const refresh = async (state) => {
    setLoading(true);
    try {
      const data = await listJobs(state || undefined);
      setJobs(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = async (jobId) => {
    if (!confirm("Discard this job? It won't be committed to the DB.")) return;
    try {
      await discardJob(jobId);
      await refresh(filter);
    } catch (e) {
      alert(e?.response?.data?.detail || "Discard failed");
    }
  };

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-bg-main py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Data ingestion</h1>
            <p className="text-text-secondary text-sm">
              Upload, review, and commit cricket data — players, matches, scorecards.
            </p>
          </div>
          <Link
            to="/ingest/new"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-medium transition"
          >
            New upload
          </Link>
        </div>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        <div className="flex gap-2 mb-4 flex-wrap">
          {STATE_FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1 rounded-md text-sm border transition ${
                filter === f.value
                  ? "bg-bg-primary text-text-primary border-bg-secondary"
                  : "bg-transparent text-text-secondary border-bg-secondary hover:bg-bg-primary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-bg-primary rounded-xl border border-bg-secondary overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-text-secondary">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-text-secondary">
              No jobs yet. Click <span className="text-text-primary">New upload</span> to start.
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-bg-secondary/50 text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">Cost</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-bg-secondary/40 hover:bg-bg-secondary/20">
                    <td className="px-4 py-3 text-text-primary truncate max-w-xs" title={j.source_filename}>
                      {j.source_filename || "(unnamed)"}
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">{j.source_kind}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded border ${STATE_PILL[j.state] || ""}`}>
                        {j.state}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">
                      ${parseFloat(j.cost_usd || 0).toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">
                      {new Date(j.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/ingest/jobs/${j.id}`}
                        className="text-blue-400 hover:text-blue-300 text-sm mr-3"
                      >
                        Open
                      </Link>
                      {j.state !== "COMMITTED" && j.state !== "DISCARDED" && (
                        <button
                          onClick={() => handleDiscard(j.id)}
                          className="text-rose-400 hover:text-rose-300 text-sm"
                        >
                          Discard
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
